import React, { useEffect, useRef } from 'react';
import loadable from '@loadable/component';
import { useAppDispatch } from 'Douleutaras/redux/hooks';
import { getSupportState } from 'Douleutaras/redux/data/settings/supportSlice';

import { useFloatingSupportWidget } from './useFloatingSupportWidget';

// Every route renders `SupportWidget`, but only a few have a surface. Keeping the widget
// in its own chunk keeps the view, the design-system core and its stylesheet out of the
// entry bundle. Nothing is lost on the server: the widget needs availability, which is
// fetched client-side only, so SSR and the first hydration render `null` regardless.
const FloatingSupportWidget = loadable(() => import('./FloatingSupportWidget'));

/**
 * The floating support widget, mounted once beside the routes (#5620).
 *
 * Renders nothing on a route with no configured surface, or when availability says
 * so (see `resolveFloatingSupportState`). Availability is fetched client-side only,
 * and only once a surface asks for it — the server never renders a resolved state.
 */
export const SupportWidget = () => {
  const dispatch = useAppDispatch();
  const { surface, state: widgetState } = useFloatingSupportWidget();
  const hasRevealed = useRef(false);

  const hasSurface = surface !== undefined;

  useEffect(() => {
    // The thunk's own `condition` skips the request once availability is loaded or loading.
    if (hasSurface) {
      void dispatch(getSupportState());
    }
  }, [hasSurface, dispatch]);

  if (!surface || !widgetState) {
    // tslint:disable-next-line:no-null-keyword
    return null;
  }

  return (
    <FloatingSupportWidget
      surface={surface}
      state={widgetState}
      revealed={hasRevealed.current}
      onRevealed={() => {
        hasRevealed.current = true;
      }}
    />
  );
};

export default SupportWidget;
